/**
 * GeoPdfExporter
 * 印刷用キャンバスを地理参照付き PDF (GeoPDF) として保存するユーティリティ。
 *
 * 地理参照は ISO 32000 の geospatial 拡張（ページ辞書の /VP 配列 +
 * /Measure /GEO 辞書）で埋め込む。QGIS/GDAL・Avenza Maps 等が対応する形式。
 *
 * キャンバスは MapLibre の描画そのまま = Web Mercator 投影なので、
 * /GCS には EPSG:3857 の PROJCS を指定し、/GPTS には四隅を WGS84 の
 * 緯度経度（lat,lon の順・度単位）で与える。リーダー側が lat/lon を
 * 投影座標へ変換してから線形補間するため、Mercator の非線形性が正しく扱われる。
 */

import { saveAs } from 'file-saver';

const EPSG_3857_WKT =
	'PROJCS["WGS 84 / Pseudo-Mercator",' +
	'GEOGCS["WGS 84",DATUM["WGS_1984",' +
	'SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],' +
	'AUTHORITY["EPSG","6326"]],' +
	'PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],' +
	'UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],' +
	'AUTHORITY["EPSG","4326"]],' +
	'PROJECTION["Mercator_1SP"],' +
	'PARAMETER["central_meridian",0],' +
	'PARAMETER["scale_factor",1],' +
	'PARAMETER["false_easting",0],' +
	'PARAMETER["false_northing",0],' +
	'UNIT["metre",1,AUTHORITY["EPSG","9001"]],' +
	'AXIS["X",EAST],AXIS["Y",NORTH],' +
	'AUTHORITY["EPSG","3857"]]';

const MM_TO_PT = 72 / 25.4;

// canvas を JPEG バイト列へ変換（toDataURL の base64 文字列化を避ける）
const canvasToJpegBytes = (canvas, quality) => new Promise((resolve, reject) => {
	canvas.toBlob((blob) => {
		if (!blob) {
			reject(new Error('canvas.toBlob returned null'));
			return;
		}
		blob.arrayBuffer()
			.then((buf) => resolve(new Uint8Array(buf)))
			.catch(reject);
	}, 'image/jpeg', quality);
});

/**
 * キャンバスを GeoPDF として保存する。
 *
 * @param {object} options
 * @param {HTMLCanvasElement} options.canvas   印刷用キャンバス（全面が bounds に対応していること）
 * @param {{ west, east, south, north }} options.bounds  キャンバス全体の地理範囲 (WGS84 度)
 * @param {number} options.widthMm   PDF ページ幅 (mm)
 * @param {number} options.heightMm  PDF ページ高さ (mm)
 * @param {string} options.title     PDF メタデータのタイトル
 * @param {string} options.fileName  保存ファイル名
 * @param {number} [options.jpegQuality=0.8]
 */
export async function exportGeoPdf({ canvas, bounds, widthMm, heightMm, title, fileName, jpegQuality = 0.8 }) {
	// pdf-lib はここでしか使わないため動的 import でバンドル分割する
	const { PDFDocument, PDFName, PDFString } = await import('pdf-lib');

	const jpegBytes = await canvasToJpegBytes(canvas, jpegQuality);

	const pdfDoc    = await PDFDocument.create();
	const jpegImage = await pdfDoc.embedJpg(jpegBytes);

	const pageW = widthMm  * MM_TO_PT;
	const pageH = heightMm * MM_TO_PT;
	const page  = pdfDoc.addPage([pageW, pageH]);
	page.drawImage(jpegImage, { x: 0, y: 0, width: pageW, height: pageH });

	// 地理参照辞書を構築してページ辞書の /VP に設定する。
	// context.obj() は JS 文字列を PDFName に変換するため、
	// 名前 (Type/Subtype 等) はそのまま文字列で、文字列値 (WKT/Name) は
	// PDFString.of() で明示する。
	// /Bounds /LPTS は正規化座標 (0-1)・左下原点、頂点順は LL, UL, UR, LR。
	// /GPTS は対応する地理点を lat,lon の順で並べる。
	const ctx = pdfDoc.context;
	const measure = ctx.obj({
		Type: 'Measure',
		Subtype: 'GEO',
		Bounds: [0, 0, 0, 1, 1, 1, 1, 0],
		GPTS: [
			bounds.south, bounds.west,
			bounds.north, bounds.west,
			bounds.north, bounds.east,
			bounds.south, bounds.east,
		],
		LPTS: [0, 0, 0, 1, 1, 1, 1, 0],
		GCS: ctx.obj({
			Type: 'PROJCS',
			EPSG: 3857,
			WKT: PDFString.of(EPSG_3857_WKT),
		}),
	});
	const viewport = ctx.obj({
		Type: 'Viewport',
		BBox: [0, 0, pageW, pageH],
		Name: PDFString.of('CollaboMap'),
		Measure: measure,
	});
	page.node.set(PDFName.of('VP'), ctx.obj([viewport]));

	if (title) pdfDoc.setTitle(title);
	pdfDoc.setCreator('CollaboMap');

	const pdfBytes = await pdfDoc.save();
	saveAs(new Blob([pdfBytes], { type: 'application/pdf' }), fileName);
}
